// CSV + PDF export utilities — XSS-safe (uses Blob URLs, no document.write)

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = String(str ?? '');
  return div.innerHTML;
}

export function downloadCSV(filename, headers, rows) {
  const escape = (val) => {
    const str = String(val ?? '');
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const csv = [
    headers.map(escape).join(','),
    ...rows.map(row => row.map(escape).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function exportIngredientsCSV(ingredients) {
  const headers = ['Name', 'Vendor', 'Category', 'Purchase Cost', 'Purchase Qty', 'Unit', 'Yield %', 'Waste %', 'Cost/Usable Unit', 'Notes'];
  const rows = ingredients.map(i => [
    i.name, i.vendor, i.category,
    (i.purchaseCost || 0).toFixed(2),
    i.purchaseQuantity, i.purchaseUnit,
    i.yieldPercent, i.wastePercent,
    (i.costPerUsableUnit || 0).toFixed(4),
    i.notes || '',
  ]);
  downloadCSV('pizzalator-ingredients', headers, rows);
}

export function exportRecipesCSV(recipes) {
  const headers = ['Name', 'Category', 'Menu Price', 'Ingredient Cost', 'Labor Cost', 'Packaging', 'Total Cost', 'Gross Profit', 'Gross Margin %', 'Food Cost %', 'Prime Cost %'];
  const rows = recipes.map(r => [
    r.name, r.category,
    (r.menuPrice || 0).toFixed(2),
    (r.ingredientCost || 0).toFixed(2),
    (r.laborCost || 0).toFixed(2),
    (r.packagingCost || 0).toFixed(2),
    (r.totalCost || 0).toFixed(2),
    (r.grossProfit || 0).toFixed(2),
    (r.grossMargin || 0).toFixed(1),
    (r.foodCostPercent || 0).toFixed(1),
    (r.primeCostPercent || 0).toFixed(1),
  ]);
  downloadCSV('pizzalator-recipes', headers, rows);
}

export function exportPnlCSV(pnlData, calculations) {
  const headers = ['Line Item', 'Amount'];
  const rows = [
    ['Revenue', (Number(pnlData.revenue) || 0).toFixed(2)],
    ['COGS', (Number(pnlData.cogs) || 0).toFixed(2)],
    ['Labor', (Number(pnlData.labor) || 0).toFixed(2)],
    ['Rent', (Number(pnlData.rent) || 0).toFixed(2)],
    ['Utilities', (Number(pnlData.utilities) || 0).toFixed(2)],
    ['Marketing', (Number(pnlData.marketing) || 0).toFixed(2)],
    ['Software', (Number(pnlData.software) || 0).toFixed(2)],
    ['Insurance', (Number(pnlData.insurance) || 0).toFixed(2)],
    ['', ''],
    ['Total Expenses', calculations.expenses.toFixed(2)],
    ['Net Profit/Loss', calculations.netProfit.toFixed(2)],
    ['Profit Margin %', calculations.profitMargin.toFixed(1)],
    ['COGS %', calculations.cogsPercent.toFixed(1)],
    ['Labor %', calculations.laborPercent.toFixed(1)],
  ];
  downloadCSV('pizzalator-pnl-report', headers, rows);
}

function openPrintWindow(title, contentHTML) {
  const e = escapeHtml;
  const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@400;600;700&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Outfit', sans-serif; padding: 40px; color: #2A2A2A; background: #fff; }
  h1 { font-family: 'Bebas Neue', cursive; font-size: 2.5rem; color: #E23D28; margin-bottom: 4px; }
  h2 { font-family: 'Bebas Neue', cursive; font-size: 1.5rem; margin: 20px 0 8px; }
  .subtitle { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
  .brand { display: flex; align-items: center; gap: 8px; margin-bottom: 20px; }
  .brand-dot { width: 32px; height: 32px; background: #E23D28; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 1rem; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th { background: #2A2A2A; color: #fff; padding: 8px 12px; text-align: left; font-family: 'Bebas Neue', cursive; font-size: 0.9rem; }
  td { padding: 8px 12px; border-bottom: 1px solid #eee; font-size: 0.85rem; }
  tr:hover td { background: #FFF8E7; }
  .total-row td { font-weight: 700; border-top: 2px solid #2A2A2A; background: #FFF8E7; }
  .profit { color: #2E7D32; font-weight: 700; } .loss { color: #E23D28; font-weight: 700; }
  .footer { margin-top: 30px; padding-top: 16px; border-top: 2px solid #eee; text-align: center; color: #999; font-size: 0.75rem; }
  @media print { body { padding: 20px; } }`;

  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${e(title)}</title><style>${styles}</style></head><body>
<div class="brand"><div class="brand-dot">P</div><div><strong>Mikey Mozzarella's Pizza-lator</strong></div></div>
<h1>${e(title)}</h1>
<p class="subtitle">Generated ${e(dateStr)}</p>
${contentHTML}
<div class="footer">Mikey Mozzarella's Pizza-lator &mdash; Know Your Dough.</div>
</body></html>`;

  const blob = new Blob([fullHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'width=900,height=700');
  if (win) {
    setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 500);
  } else {
    URL.revokeObjectURL(url);
  }
}

export function printReport(title, contentHTML) {
  openPrintWindow(title, contentHTML);
}

export function printPnlReport(pnlData, calculations) {
  const e = escapeHtml;
  const rows = [
    ['Revenue', `$${(Number(pnlData.revenue) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Cost of Goods Sold', `$${(Number(pnlData.cogs) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Labor', `$${(Number(pnlData.labor) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Rent', `$${(Number(pnlData.rent) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Utilities', `$${(Number(pnlData.utilities) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Marketing', `$${(Number(pnlData.marketing) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Software', `$${(Number(pnlData.software) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
    ['Insurance', `$${(Number(pnlData.insurance) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`],
  ];
  const html = `<table><tr><th>Line Item</th><th style="text-align:right">Amount</th></tr>
    ${rows.map(([label, val]) => `<tr><td>${e(label)}</td><td style="text-align:right">${val}</td></tr>`).join('')}
    <tr class="total-row"><td>Total Expenses</td><td style="text-align:right">$${calculations.expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td></tr>
    <tr class="total-row"><td>Net ${calculations.netProfit >= 0 ? 'Profit' : 'Loss'}</td><td style="text-align:right" class="${calculations.netProfit >= 0 ? 'profit' : 'loss'}">$${calculations.netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td></tr>
    </table>
    <h2>Key Metrics</h2>
    <table><tr><th>Metric</th><th style="text-align:right">Value</th></tr>
    <tr><td>Profit Margin</td><td style="text-align:right">${calculations.profitMargin.toFixed(1)}%</td></tr>
    <tr><td>COGS %</td><td style="text-align:right">${calculations.cogsPercent.toFixed(1)}%</td></tr>
    <tr><td>Labor %</td><td style="text-align:right">${calculations.laborPercent.toFixed(1)}%</td></tr>
    </table>`;
  openPrintWindow('P&L Snapshot', html);
}

export function printRecipesReport(recipes) {
  const e = escapeHtml;
  const html = `<table><tr><th>Recipe</th><th>Category</th><th style="text-align:right">Menu Price</th><th style="text-align:right">Total Cost</th><th style="text-align:right">Profit</th><th style="text-align:right">Food Cost %</th><th style="text-align:right">Margin %</th></tr>
    ${recipes.map(r => `<tr><td>${e(r.name)}</td><td>${e(r.category)}</td>
      <td style="text-align:right">$${(r.menuPrice || 0).toFixed(2)}</td>
      <td style="text-align:right">$${(r.totalCost || 0).toFixed(2)}</td>
      <td style="text-align:right" class="${(r.grossProfit || 0) >= 0 ? 'profit' : 'loss'}">$${(r.grossProfit || 0).toFixed(2)}</td>
      <td style="text-align:right">${(r.foodCostPercent || 0).toFixed(1)}%</td>
      <td style="text-align:right">${(r.grossMargin || 0).toFixed(1)}%</td></tr>`).join('')}
    </table>`;
  openPrintWindow('Recipe Cost Report', html);
}

export function printIngredientsReport(ingredients) {
  const e = escapeHtml;
  const html = `<table><tr><th>Ingredient</th><th>Vendor</th><th>Category</th><th style="text-align:right">Cost</th><th style="text-align:right">Qty</th><th>Unit</th><th style="text-align:right">Yield %</th><th style="text-align:right">Cost/Unit</th></tr>
    ${ingredients.map(i => `<tr><td>${e(i.name)}</td><td>${e(i.vendor || '-')}</td><td>${e(i.category)}</td>
      <td style="text-align:right">$${(i.purchaseCost || 0).toFixed(2)}</td>
      <td style="text-align:right">${i.purchaseQuantity}</td><td>${e(i.purchaseUnit)}</td>
      <td style="text-align:right">${i.yieldPercent}%</td>
      <td style="text-align:right">$${(i.costPerUsableUnit || 0).toFixed(4)}</td></tr>`).join('')}
    </table>`;
  openPrintWindow('Ingredient Database Report', html);
}
